/**
 * NoteAddTab.jsx — Muhasebe FAZ 4.8, "Senet Ekle" iç sekmesi.
 *
 * Uç: POST /admin/promissory-notes
 * `invoice_id` ZORUNLU (backend A0-3/INV-8: id'siz senedin tahsilatı para ile
 * fatura arasındaki bağı kopartıyordu) — bu yüzden fatura seçimi serbest
 * metin değil, öğrencinin MEVCUT faturalarından seçilir.
 *
 * `satis_personel_id` body'ye KONULMAZ: backend onu faturaya bağlı
 * ödeme/taksit kayıtlarından türetir (IDOR sınıfı, PLAN §6.2 madde 1).
 *
 * ── S-14 taksit bağı (FAZ 4 PAKET H) ────────────────────────────────────────
 * Borç TEK yerde durur: `installments`. Senet borcun kendisi değil, taksidin
 * hukuki temsilidir ve `installment_id` ile ona bağlanır. Backend bu alanı
 * PAKET F ile kabul etmeye başladı; bu form onu artık GÖNDERİYOR.
 *
 * Bağ OPSİYONELDİR (backend'de de öyle: taksidi olmayan eski/serbest borç
 * senetleri meşru) — ama SESSİZ bir varsayılan bırakılmaz: seçim boşken
 * ekranda bağsız senedin ne anlama geldiği açıkça yazar.
 *
 * Aday taksitler NoteMergePanel'deki INV-5 deseniyle aynı mantıkla baştan
 * kısıtlanır (linkableInstallments): admin'e 400/409 yedirilmez. Taksit
 * seçilince tutar ve vade taksitten doldurulur, İKİSİ DE KİLİTLENİR —
 * backend tutar eşitliğini ±0,005 ile arıyor (400
 * `senet_taksit_tutari_uyusmuyor`), FAZ 4 PAKET I / reviewer O-6 ile birlikte
 * VADE BİREBİRLİĞİNİ de zorunlu tuttu (400 `senet_taksit_vadesi_uyusmuyor`,
 * MUHASEBE_VERI_MODELI.md §1.11). Vade alanı önceden serbest bırakılıyor ve
 * "değiştirebilirsiniz" ipucu veriliyordu — bu ipucu artık YANLIŞTI.
 */
import { useEffect, useMemo, useState } from 'react';
import { FilePlus2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { fmtMoneyAccounting } from '../../../utils/money';
import { useAccountingBanks, useCreatePromissoryNote } from '../../../api/queries/accounting';
import {
  ActionButton,
  Field,
  INPUT_CLASS,
  InlineAlert,
  TabIntro,
  installmentOptionLabel,
  noteErrorMessage,
} from './noteUiKit';
import {
  interestBlockedInstallments,
  isValidDueDate,
  linkableInstallments,
  normalizeDueDate,
} from './promissoryMath';

const EMPTY = {
  invoice_id: '',
  installment_id: '',
  amount: '',
  due_date: '',
  note_number: '',
  description: '',
  bank_id: '',
  course_id: '',
};

export default function NoteAddTab({ userId, detail }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const createNote = useCreatePromissoryNote(userId);
  const { data: banks = [], isLoading: banksLoading, isError: banksError } = useAccountingBanks();

  const [values, setValues] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);

  const invoices = useMemo(() => detail?.invoices || [], [detail]);
  const enrollments = useMemo(() => detail?.enrollments || [], [detail]);
  const installments = useMemo(() => detail?.installments || [], [detail]);
  const notes = useMemo(() => detail?.promissory_notes || [], [detail]);

  const set = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));

  // Aday taksitler: seçili faturaya ait, terminal olmayan, başka aktif/icra
  // senedi tarafından TUTULMAYAN ve faiz tahakkuk etmemiş taksitler.
  const eligibleInstallments = useMemo(
    () => linkableInstallments(installments, notes, values.invoice_id, userId),
    [installments, notes, values.invoice_id, userId]
  );
  // Yalnızca faiz tahakkuku yüzünden düşenler — sessizce yok sayılmaz.
  const interestBlocked = useMemo(
    () => interestBlockedInstallments(installments, notes, values.invoice_id, userId),
    [installments, notes, values.invoice_id, userId]
  );

  const selectedInstallment = useMemo(
    () => eligibleInstallments.find((i) => i.installment_id === values.installment_id) || null,
    [eligibleInstallments, values.installment_id]
  );

  // Fatura değişince taksit seçimi düşer (taksit faturaya bağlıdır; aksi halde
  // backend `senet_taksit_faturasi_uyusmuyor` ile 400 dönerdi).
  useEffect(() => {
    setValues((prev) => {
      if (!prev.installment_id) return prev;
      const stillValid = eligibleInstallments.some((i) => i.installment_id === prev.installment_id);
      return stillValid ? prev : { ...prev, installment_id: '' };
    });
  }, [eligibleInstallments]);

  /**
   * Taksit seçimi: tutar + vade taksitten DOLDURULUR ve ikisi de kilitlenir.
   * Backend tutarı ±0,005 ile, vadeyi ise BİREBİR eşitlikle doğruluyor;
   * ekranda gösterilen değer sunucunun karşılaştıracağı değerin ta kendisi
   * olmalı, bu yüzden `prev` değerine geri düşülmez (boş vadeli taksitte
   * alan da boş kalır — uydurma bir tarih gönderilmez).
   */
  const onInstallmentChange = (e) => {
    const id = e.target.value;
    const inst = eligibleInstallments.find((i) => i.installment_id === id) || null;
    setValues((prev) => ({
      ...prev,
      installment_id: id,
      amount: inst ? String(inst.amount ?? '') : prev.amount,
      due_date: inst ? normalizeDueDate(inst.due_date) : prev.due_date,
    }));
  };

  const amountNumber = Number(values.amount);
  const invoiceMissing = !values.invoice_id;
  const amountInvalid = values.amount === '' || !Number.isFinite(amountNumber) || amountNumber <= 0;
  const dueInvalid = !!values.due_date && !isValidDueDate(values.due_date);
  const canSubmit = !invoiceMissing && !amountInvalid && !dueInvalid;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;

    try {
      await createNote.mutateAsync({
        invoice_id: values.invoice_id,
        // S-14: bağ opsiyonel — seçilmediyse boş string gider, backend
        // senedi bağsız oluşturur (alan her zaman yazılır).
        installment_id: values.installment_id,
        user_id: userId,
        amount: amountNumber,
        due_date: normalizeDueDate(values.due_date),
        note_number: values.note_number,
        description: values.description,
        bank_id: values.bank_id,
        course_id: values.course_id,
      });
      toast.success(t('accounting.notes.add.success'));
      setValues(EMPTY);
      setSubmitted(false);
    } catch (err) {
      toast.error(noteErrorMessage(err, lang, t));
    }
  };

  if (invoices.length === 0) {
    return (
      <div className="space-y-4">
        <TabIntro title={t('accounting.notes.add.title')} description={t('accounting.notes.add.description')} />
        <InlineAlert variant="info" testId="note-add-no-invoice">
          {t('accounting.notes.add.no_invoice')}
        </InlineAlert>
      </div>
    );
  }

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <TabIntro title={t('accounting.notes.add.title')} description={t('accounting.notes.add.description')} />

      <Field
        id="note-add-invoice"
        label={t('accounting.notes.add.invoice_label')}
        required
        hint={t('accounting.notes.add.invoice_hint')}
        error={submitted && invoiceMissing ? t('accounting.notes.add.invoice_required') : ''}
      >
        {({ id, describedBy }) => (
          <select
            id={id}
            className={INPUT_CLASS}
            value={values.invoice_id}
            aria-required
            aria-invalid={submitted && invoiceMissing ? true : undefined}
            aria-describedby={describedBy}
            onChange={set('invoice_id')}
            data-testid="note-add-invoice-select"
          >
            <option value="">{t('accounting.notes.add.invoice_placeholder')}</option>
            {invoices.map((inv) => (
              <option key={inv.invoice_id} value={inv.invoice_id}>
                {`${inv.invoice_no || inv.invoice_id} · ${fmtMoneyAccounting(inv.total_amount, lang)}`}
              </option>
            ))}
          </select>
        )}
      </Field>

      {/* S-14 — taksit bağı. Seçici HER ZAMAN görünür: aday yoksa bile
          gizlenmez, gerekçesi altında bilgi olarak yazılır. */}
      <Field
        id="note-add-installment"
        label={t('accounting.notes.add.installment_label')}
        hint={t('accounting.notes.add.installment_hint')}
      >
        {({ id, describedBy }) => (
          <select
            id={id}
            className={INPUT_CLASS}
            value={values.installment_id}
            disabled={invoiceMissing || eligibleInstallments.length === 0}
            aria-describedby={describedBy}
            onChange={onInstallmentChange}
            data-testid="note-add-installment-select"
          >
            <option value="">{t('accounting.notes.add.installment_placeholder')}</option>
            {eligibleInstallments.map((inst) => (
              <option key={inst.installment_id} value={inst.installment_id}>
                {installmentOptionLabel(inst, lang, t)}
              </option>
            ))}
          </select>
        )}
      </Field>

      {invoiceMissing && (
        <InlineAlert variant="info" testId="note-add-installment-pick-invoice">
          {t('accounting.notes.add.installment_pick_invoice')}
        </InlineAlert>
      )}

      {!invoiceMissing && eligibleInstallments.length === 0 && (
        <InlineAlert variant="info" testId="note-add-installment-empty">
          {t('accounting.notes.add.installment_none_eligible')}
        </InlineAlert>
      )}

      {interestBlocked.length > 0 && (
        <InlineAlert
          variant="warning"
          title={t('accounting.notes.add.installment_interest_title')}
          testId="note-add-installment-interest"
        >
          {`${interestBlocked.length} · ${t('accounting.notes.add.installment_interest_desc')}`}
        </InlineAlert>
      )}

      {/* Bağ boşken ne olduğu AÇIKÇA yazar — sessiz varsayılan yok. */}
      {!values.installment_id && !invoiceMissing && (
        <InlineAlert
          variant="warning"
          title={t('accounting.notes.add.unlinked_title')}
          testId="note-add-unlinked-warning"
        >
          {t('accounting.notes.add.unlinked_desc')}
        </InlineAlert>
      )}

      {selectedInstallment && (
        <InlineAlert
          variant="info"
          title={t('accounting.notes.add.linked_title')}
          testId="note-add-linked-info"
        >
          {t('accounting.notes.add.linked_desc')}
        </InlineAlert>
      )}

      <Field
        id="note-add-amount"
        label={t('accounting.notes.amount_label')}
        required
        hint={selectedInstallment ? t('accounting.notes.add.amount_locked_hint') : ''}
        error={submitted && amountInvalid ? t('accounting.notes.amount_invalid') : ''}
      >
        {({ id, describedBy }) => (
          <input
            id={id}
            type="number"
            step="0.01"
            min="0"
            className={INPUT_CLASS}
            value={values.amount}
            readOnly={!!selectedInstallment}
            aria-required
            aria-readonly={selectedInstallment ? true : undefined}
            aria-invalid={submitted && amountInvalid ? true : undefined}
            aria-describedby={describedBy}
            onChange={set('amount')}
            data-testid="note-add-amount-input"
          />
        )}
      </Field>

      <Field
        id="note-add-due"
        label={t('accounting.notes.due_label')}
        hint={selectedInstallment ? t('accounting.notes.add.due_locked_hint') : ''}
        error={dueInvalid ? t('accounting.notes.due_invalid') : ''}
      >
        {({ id, describedBy }) => (
          <input
            id={id}
            type="date"
            className={INPUT_CLASS}
            value={values.due_date}
            readOnly={!!selectedInstallment}
            aria-readonly={selectedInstallment ? true : undefined}
            aria-invalid={dueInvalid ? true : undefined}
            aria-describedby={describedBy}
            onChange={(e) => { if (!selectedInstallment) set('due_date')(e); }}
            data-testid="note-add-due-input"
          />
        )}
      </Field>

      <Field id="note-add-number" label={t('accounting.notes.number_label')} hint={t('accounting.notes.number_hint')}>
        {({ id }) => (
          <input
            id={id}
            type="text"
            className={INPUT_CLASS}
            value={values.note_number}
            onChange={set('note_number')}
            data-testid="note-add-number-input"
          />
        )}
      </Field>

      <Field id="note-add-course" label={t('accounting.notes.course_label')}>
        {({ id }) => (
          <select
            id={id}
            className={INPUT_CLASS}
            value={values.course_id}
            onChange={set('course_id')}
            data-testid="note-add-course-select"
          >
            <option value="">{t('accounting.collect_form.course_general')}</option>
            {enrollments.map((en) => (
              <option key={en.course_id} value={en.course_id}>
                {en.course_title || en.title || en.course_id}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field id="note-add-bank" label={t('accounting.notes.bank_label')} hint={t('accounting.notes.bank_hint')}>
        {({ id }) => (
          <select
            id={id}
            className={INPUT_CLASS}
            value={values.bank_id}
            disabled={banksLoading}
            onChange={set('bank_id')}
            data-testid="note-add-bank-select"
          >
            <option value="">{t('accounting.notes.bank_none')}</option>
            {banks.map((b) => (
              <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
            ))}
          </select>
        )}
      </Field>

      {banksError && (
        <InlineAlert variant="warning" testId="note-add-banks-error">
          {t('accounting.notes.banks_error')}
        </InlineAlert>
      )}

      <Field id="note-add-description" label={t('accounting.notes.description_label')}>
        {({ id }) => (
          <input
            id={id}
            type="text"
            className={INPUT_CLASS}
            value={values.description}
            placeholder={t('accounting.notes.description_placeholder')}
            onChange={set('description')}
            data-testid="note-add-description-input"
          />
        )}
      </Field>

      <div className="flex justify-end">
        <ActionButton
          type="submit"
          variant="success"
          icon={FilePlus2}
          loading={createNote.isPending}
          disabled={createNote.isPending}
          testId="note-add-submit"
        >
          {createNote.isPending ? t('accounting.notes.submitting') : t('accounting.notes.add.submit')}
        </ActionButton>
      </div>
    </form>
  );
}
